import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import CategorySelect from 'common/components/CategorySelect';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChartDataset, Plugin } from 'chart.js';

import DashboardCard from 'areas/dashboard/components/DashboardCard';
import { PageSection } from 'common/styles/page';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

import {
  useApiPortalV1AssessmentsResultsListQuery,
  useGetAssessmentPeriodMetricBenchmarkResultsQuery,
} from 'areas/assessment-schedule/api';
import styled from 'styled-components';

import { hexToRgb } from 'common/styles/colors';
import { AggregateType } from 'common/constants/AggregateType';
import {
  getSelectedAssessmentPeriodCategories,
  getSelectedAssessmentPeriod,
  getSelectedAssessmentPeriodId,
  isNulogicBenchmarksEnabled,
} from 'common/selectors/auth';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

interface AnalyticsPillarsBarProps {
  selectedPillar: number | null;
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  setSelectedPillar: (pillar: number | null) => void;
}

const AnalyticsPillarsBar: React.FC<AnalyticsPillarsBarProps> = ({
  selectedPillar,
  selectedCategory,
  setSelectedPillar,
  setSelectedCategory,
}) => {
  const assessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const assessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const assessmentPeriodCategories = useSelector(getSelectedAssessmentPeriodCategories) as any[];
  const isBenchmarkFeatureEnabled = useSelector(isNulogicBenchmarksEnabled);
  const assessmentPeriodName = assessmentPeriod?.name;

  const { data: data_by_option } = useApiPortalV1AssessmentsResultsListQuery({
    aggregateType: AggregateType.CATEGORY_PILLARS,
    assessmentPeriodId: assessmentPeriodId,
  });
  const { data: benchmarkData } = useGetAssessmentPeriodMetricBenchmarkResultsQuery(
    { assessmentPeriodId: assessmentPeriodId },
    { skip: !assessmentPeriodId || !isBenchmarkFeatureEnabled }
  );

  type PillarOption = { value: number; label: string };
  const [filterPillars, setFilterPillars] = useState<PillarOption[]>([]);
  const [showBenchmarkLine, setShowBenchmarkLine] = useState(false);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  interface CustomBarDataset extends ChartDataset<'bar'> {
    nValues?: number[];
  }
  const [barChartData, setBarChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Scores',
        data: [],
        nValues: [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 0.8)',
      },
    ],
  });

  useEffect(() => {
    if (data_by_option && data_by_option.length > 0) {
      const pillars: PillarOption[] = Array.from(
        new Map<string, PillarOption>(
          data_by_option[0]?.result?.pillars.map(
            (score: { pillar_name: string; pillar: number }) => [
              score.pillar,
              { value: score.pillar, label: score.pillar_name },
            ]
          )
        ).values()
      );
      setFilterPillars(pillars);

      const selectedPillarExists = pillars.some(pillar => pillar.value === selectedPillar);
      if (!selectedPillar || !selectedPillarExists || selectedPillar === 0) {
        setSelectedPillar(pillars[0].value);
      }
    }
  }, [data_by_option]);

  const learnMoreItems = [
    {
      term: 'Pillars',
      description:
        'Choose the cultural pillar (e.g., Inclusion, Representation, ...) to display in the graph. This selection also determines the pillars cooresponding survey questions shown below.',
    },
    {
      term: 'Demographics',
      description:
        'Switch between demographics like teams, locations, gender, and ethnicity to compare scores across different segments of your population.',
    },
    {
      term: 'Pillar Questions',
      description:
        'This section displays the survey questions that were asked to calculate the selected pillar score. Each question shows the average score and a visual slider with a colored bar that shows the standard deviation. Click on the question to see question averages further segmented by demographic group.',
    },
  ];


  useEffect(() => {
    if (selectedPillar !== null && selectedCategory && assessmentPeriodCategories.length > 0) {

      setPagePrompts([
        {
          title: 'Pillar Score Insights',
          displayText: `Tell me something about my ${filterPillars.find(p => p.value === selectedPillar)?.label} data across ${assessmentPeriodCategories.find(c => c.id === selectedCategory)?.name}.`,
          prompt: `Analyze the assessment results for the ${filterPillars.find(p => p.value === selectedPillar)?.label} pillar in the ${assessmentPeriodCategories.find(c => c.id === selectedCategory)?.name} category. Summarize performance across demographics, highlight strong and weak groups, note any outliers or patterns.`,
        },
        {
          title: 'Demographic Performance Summary',
          displayText: `How is the ${filterPillars.find(p => p.value === selectedPillar)?.label} pillar performing for different demographic segments in ${assessmentPeriodCategories.find(c => c.id === selectedCategory)?.name}?`,
          prompt: `Summarize the current ${filterPillars.find(p => p.value === selectedPillar)?.label} pillar performance in ${assessmentPeriodCategories.find(c => c.id === selectedCategory)?.name}. Focus on which demographic segments are strongest, which are weakest, and any notable trends or groups that stand out in the data.`,
        },
        {
          title: 'Question-Level Insights',
          displayText: `Help me understand the question-level results table for the ${filterPillars.find(p => p.value === selectedPillar)?.label} pillar considering ${assessmentPeriodCategories.find(c => c.id === selectedCategory)?.name}.`,
          prompt: `Review question-level results for the ${filterPillars.find(p => p.value === selectedPillar)?.label} pillar in ${assessmentPeriodCategories.find(c => c.id === selectedCategory)?.name}. Explain what the individual question scores reveal about strengths and weaknesses, and how those findings relate to the overall pillar score trends shown above.`,
        },
      ]);
      setPageInfoTitle('About Pillar Scores');
      setPageInfoIntro(
        'This area shows how different demographic groups within the organization are performing across key cultural pillars like Inclusion, Representation, and Psychological Safety. Visualizing culture scores and diving into the survey questions that support them can quickly identify strengths, areas for improvement, and patterns that warrant further attention.'
      );
      setPageInfoItems(learnMoreItems);
    } else {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    }

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [
    selectedPillar,
    selectedCategory,
    assessmentPeriodCategories,
    filterPillars,
    setPagePrompts,
    setPageInfoItems,
    setPageInfoTitle,
    setPageInfoIntro,
  ]);

  useEffect(() => {
    if (data_by_option && selectedPillar !== null) {
      const filteredOptionData = data_by_option[0]?.result?.pillars.filter(
        (factor: { pillar: number; category_id: string }) =>
          factor.pillar === selectedPillar && factor.category_id === selectedCategory
      );

      const backgroundColors = filteredOptionData.map((score: { color: string }) =>
        hexToRgb(score.color, 0.6)
      );
      const borderColors = filteredOptionData.map((score: { color: string }) =>
        hexToRgb(score.color, 0.8)
      );

      const updatedBarChartData = {
        labels: filteredOptionData.map((score: { option_name: string }) => score.option_name),
        datasets: [
          {
            label: 'Scores',
            data: filteredOptionData.map((score: { overall_score: number }) =>
              score.overall_score.toFixed(1)
            ),
            nValues: filteredOptionData.map((score: { n: number }) => score.n),
            backgroundColor: backgroundColors,
            borderColor: borderColors,
          },
        ],
      };
      setBarChartData(updatedBarChartData);
    }
  }, [data_by_option, selectedPillar, selectedCategory]);

  const datasets: CustomBarDataset[] = [
    {
      label: 'Scores',
      data: barChartData.datasets[0].data,
      nValues: barChartData.datasets[0].nValues,
      backgroundColor: barChartData.datasets[0].backgroundColor,
      borderColor: barChartData.datasets[0].borderColor,
      borderWidth: 2,
    },
  ];

  const selectedPillarLabel =
    filterPillars.find(pillar => Number(pillar.value) === Number(selectedPillar))?.label || '';

  const normalizedSelectedPillarLabel = selectedPillarLabel.trim().toLowerCase();
  const benchmarkPillars = isBenchmarkFeatureEnabled ? benchmarkData?.pillars ?? [] : [];

  const benchmarkPillarByLabel = normalizedSelectedPillarLabel
    ? benchmarkPillars.find(
        pillar =>
          typeof pillar.label === 'string' &&
          pillar.label.trim().toLowerCase() === normalizedSelectedPillarLabel
      )
    : undefined;

  const benchmarkPillarByFactor = benchmarkPillars.find(pillar => {
    const pillarFactor = Number(pillar.factor);
    const selectedFactor = Number(selectedPillar);

    return Number.isFinite(pillarFactor) && Number.isFinite(selectedFactor)
      ? pillarFactor === selectedFactor
      : false;
  });

  // Prefer matching by pillar label to avoid cross-endpoint factor drift.
  const benchmarkPillar = benchmarkPillarByLabel ?? benchmarkPillarByFactor;
  const benchmarkPillarMean = benchmarkPillar?.mean;
  const hasBenchmarkPillarMean =
    typeof benchmarkPillarMean === 'number' && Number.isFinite(benchmarkPillarMean);

  const benchmarkLineOverlayPlugin: Plugin<'bar'> = {
    id: 'benchmarkLineOverlay',
    afterDraw: (chart, _args, pluginOptions) => {
      const options = (pluginOptions || {}) as {
        enabled?: boolean;
        value?: number;
        color?: string;
      };

      if (!options.enabled || typeof options.value !== 'number' || !Number.isFinite(options.value)) {
        return;
      }

      const yScale = chart.scales.y;
      const chartArea = chart.chartArea;
      if (!yScale || !chartArea) {
        return;
      }

      const y = yScale.getPixelForValue(options.value);

      const ctx = chart.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([6, 6]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = options.color || 'rgba(230, 101, 121, 0.95)';
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      ctx.restore();
    },
  };

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      benchmarkLineOverlay: {
        enabled: hasBenchmarkPillarMean && showBenchmarkLine,
        value: benchmarkPillarMean,
        color: 'rgba(230, 101, 121, 0.95)',
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: any) => {
            const dataset = tooltipItem.dataset as CustomBarDataset;
            const index = tooltipItem.dataIndex;
            const value = dataset.data[index];
            const n = dataset.nValues?.[index] ?? 0;

            return `Score: ${value} (${n} respondents)`;
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 10,
      },
    },
  };

  return (
    <DashboardCard
      title={`${assessmentPeriodName}`}
      height="500px"
      controls={
        <>
          <Select
            options={filterPillars}
            placeholder="Select Pillar"
            value={filterPillars.find(option => option.value === selectedPillar)}
            onChange={option => setSelectedPillar(option ? option.value : null)}
            styles={{
              container: base => ({
                ...base,
                minWidth: '200px',
                maxWidth: '280px',
              }),
            }}
          />
          <CategorySelect
            categories={assessmentPeriodCategories as any}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            placeholder="Select Category"
            width="250px"
          />
        </>
      }
    >
      <PageSection
        style={{
          width: '100%',
          height: '100%',
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          paddingTop: '20px',
          overflow: 'hidden',
        }}
      >
        <ChartArea>
          <ChartCanvasArea>
            <Bar
              plugins={[benchmarkLineOverlayPlugin]}
              options={chartOptions}
              data={{
                labels: barChartData.labels,
                datasets: datasets,
              }}
              style={{ height: '100%' }}
            />
          </ChartCanvasArea>
          {isBenchmarkFeatureEnabled && hasBenchmarkPillarMean && (
            <BenchmarkControlRow>
              <BenchmarkToggle
                role="button"
                tabIndex={0}
                aria-label="Toggle nuLogic Benchmark"
                aria-pressed={showBenchmarkLine}
                onClick={() => setShowBenchmarkLine(prev => !prev)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setShowBenchmarkLine(prev => !prev);
                  }
                }}
              >
                {!showBenchmarkLine && (
                  <BenchmarkCheckbox
                    type="checkbox"
                    checked={false}
                    readOnly
                    tabIndex={-1}
                    aria-label="Show nuLogic Benchmark"
                  />
                )}
                <BenchmarkLegendSwatch hidden={!showBenchmarkLine} />
                <span>{`nuLogic Benchmark (${benchmarkPillarMean.toFixed(1)})`}</span>
              </BenchmarkToggle>
            </BenchmarkControlRow>
          )}
        </ChartArea>
      </PageSection>
    </DashboardCard>
  );
};

export default AnalyticsPillarsBar;

const BenchmarkToggle = styled.div`
  display: inline-flex;
  align-items: center;
  height: 38px;
  gap: 8px;
  white-space: nowrap;
  border: none;
  border-radius: 0;
  background: #fff;
  color: #666;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 10px;
  box-sizing: border-box;
`;

const BenchmarkCheckbox = styled.input`
  cursor: pointer;
`;

const BenchmarkLegendSwatch = styled.span<{ hidden: boolean }>`
  width: 22px;
  height: 0;
  border-top: 2px dashed rgba(230, 101, 121, 0.95);
  opacity: ${({ hidden }) => (hidden ? 0.3 : 1)};
`;

const BenchmarkControlRow = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 8px;
  padding-bottom: 2px;
`;

const ChartArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ChartCanvasArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
`;


